import { Locale, TranslateParams } from "i18n/locale";
import { formatAmount } from "utils/formatAmount";

/**
 * Builds the recap shown under the AI search bar as a readable sentence —
 * "2 pet-friendly properties in Brussels near Mont des Arts with internet".
 *
 * Composed from state the app already holds rather than asked of the AI: the
 * count has to be exact, and a locally built string is instant, free, and can't
 * invent a number or drift into the wrong language.
 *
 * Word order is the dictionary's job, not this file's. English puts adjectives
 * before the noun ("2 pet-friendly properties") and French after it ("2
 * propriétés acceptant les animaux"), so the head of the sentence is a single
 * key per language with the placeholders in whatever order that language wants.
 * Each trailing clause likewise carries its own leading separator, so French
 * can use a comma where English uses a space.
 */

// id -> translation key, mirroring the option lists in MoreFiltersPanel. Copied
// rather than imported because those arrays also carry icon imports, which the
// header has no use for. If an option is added there but not here it simply
// goes unnamed in the sentence — the search itself is unaffected.
//
// Rules read as adjectives ("pet-friendly"), so they get their own wording
// rather than reusing the filter panel's noun-phrase labels ("Pets allowed").
const RULE_ADJECTIVES: Record<string, string> = {
  "Pets allowed": "header.aiAdjPets",
  "Family Friendly": "header.aiAdjFamily",
  "Smoking allowed": "header.aiAdjSmoking",
};

// Amenities read as objects of "with …", so the panel's own labels work as-is.
const AMENITY_LABELS: Record<string, string> = {
  Internet: "filters.amenities.internet",
  "Swimming Pool": "filters.amenities.swimmingPool",
  Parking: "filters.amenities.parking",
  "Washing Machine": "filters.amenities.washingMachine",
  "Drying Machine": "filters.amenities.dryingMachine",
  "Iron & Ironing Board": "filters.amenities.ironBoard",
  "Baby Cot": "filters.amenities.babyCot",
  Dishwasher: "filters.amenities.dishwasher",
  "Linen & Towels": "filters.amenities.linenTowels",
  "Satellite TV": "filters.amenities.satelliteTV",
  Terrace: "filters.amenities.terrace",
  Heating: "filters.amenities.heating",
  "Air Conditioning": "filters.amenities.airConditioning",
  "Cable TV": "filters.amenities.cableTV",
  Garden: "filters.amenities.garden",
};

// Matches AppContext's own no-filter baseline, so an untouched slider is never
// described as a price constraint.
const DEFAULT_MIN_PRICE = 0;
const DEFAULT_MAX_PRICE = 1000;

// The header is position:fixed, so every line here pushes the listings down the
// page. Naming at most two amenities and rolling the rest into "and N more"
// bounds the sentence by construction rather than by hoping a guest doesn't
// tick eight boxes.
const MAX_NAMED_AMENITIES = 2;

export interface SearchSummaryFilterValues {
  propertyRules?: string[];
  amenities?: string[];
  bedrooms?: number;
  rangePrices?: string[] | number[];
}

export interface SearchSummaryInput {
  count: number;
  /** The backend's city enum ("Paris" | "Brussels") or null for no city filter. */
  location: string | null;
  /** A "near <place>" target's name, already a proper noun — never translated. */
  nearbyPlaceName: string | null;
  filterValues: SearchSummaryFilterValues | null;
  locale: Locale;
  t: (key: string, params?: TranslateParams) => string;
}

export const buildSearchSummary = ({
  count,
  location,
  nearbyPlaceName,
  filterValues,
  locale,
  t,
}: SearchSummaryInput): string => {
  // The city goes through common.paris / common.brussels because `location`
  // holds the backend's enum ("Brussels"), which is "Bruxelles" in French —
  // interpolating it raw would leak the English name into the French sentence.
  const city =
    location === "Paris"
      ? t("common.paris")
      : location === "Brussels"
      ? t("common.brussels")
      : null;

  if (!count) {
    return city
      ? t("header.aiSummaryNone", { city })
      : t("header.aiSummaryNoneAnywhere");
  }

  const adjectives = (filterValues?.propertyRules ?? [])
    .map((id) => RULE_ADJECTIVES[id])
    .filter(Boolean)
    .map((key) => t(key))
    .join(", ");

  const noun = t(count === 1 ? "header.aiNounSingular" : "header.aiNounPlural");

  // Four head variants rather than one with optional pieces: each language then
  // owns where the adjectives and the city sit in its own sentence.
  let sentence: string;
  if (city) {
    sentence = adjectives
      ? t("header.aiSentenceAdj", { count, adjectives, noun, city })
      : t("header.aiSentence", { count, noun, city });
  } else {
    sentence = adjectives
      ? t("header.aiSentenceAnywhereAdj", { count, adjectives, noun })
      : t("header.aiSentenceAnywhere", { count, noun });
  }

  if (nearbyPlaceName) {
    // English says "near X"; French uses "secteur X", the phrasing property
    // listings actually use. Both avoid a preposition that would have to
    // contract with the place's article — "près **du** Mont des Arts" but
    // "près **de la** Grand-Place" — which a template can't know.
    sentence += t("header.aiSentenceNear", { place: nearbyPlaceName });
  }

  // Amenities are mid-sentence objects, so they lose the filter panel's leading
  // capital ("Internet" -> "with internet"). Locale-aware because the casing
  // rules differ by language.
  const amenities = (filterValues?.amenities ?? [])
    .map((id) => AMENITY_LABELS[id])
    .filter(Boolean)
    .map((key) => t(key).toLocaleLowerCase(locale === "fr" ? "fr-FR" : "en-US"));

  if (amenities.length) {
    const shown = amenities.slice(0, MAX_NAMED_AMENITIES);
    const rest = amenities.length - shown.length;
    const tail = rest > 0 ? t("header.aiListMore", { count: rest }) : null;
    const parts = tail ? [...shown, tail] : shown;
    // "a", "a and b", "a, b and c" — the last join is the language's own word.
    const list =
      parts.length === 1
        ? parts[0]
        : t("header.aiListAnd", {
            a: parts.slice(0, -1).join(", "),
            b: parts[parts.length - 1],
          });
    sentence += t("header.aiSentenceWith", { list });
  }

  const prices = filterValues?.rangePrices ?? [];
  const min = Number(prices[0]);
  const max = Number(prices[1]);
  const hasMin = Number.isFinite(min) && min > DEFAULT_MIN_PRICE;
  const hasMax = Number.isFinite(max) && max < DEFAULT_MAX_PRICE;
  const money = (value: number) => formatAmount(value, locale, 0);

  if (hasMin && hasMax) {
    sentence += t("header.aiSentencePriceRange", {
      min: money(min),
      max: money(max),
    });
  } else if (hasMax) {
    sentence += t("header.aiSentencePriceMax", { max: money(max) });
  } else if (hasMin) {
    sentence += t("header.aiSentencePriceMin", { min: money(min) });
  }

  const bedrooms = Number(filterValues?.bedrooms);
  if (Number.isFinite(bedrooms) && bedrooms > 0) {
    sentence += t("header.aiSentenceBedrooms", { count: bedrooms });
  }

  return sentence;
};
